import React from "react";
import { XIcon } from "lucide-react";
import { getParticipantColor } from "../../../utils/participantColors";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Interfaz adaptada del schema local de Participants
interface ParticipantData {
  id: string;
  name: string;
  alias?: string;
  image?: string;
  metadata?: {
    colorId?: string;
    customColor?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface ParticipantBadgeProps {
  participant: ParticipantData;
  variant?: "photo" | "pill" | "avatar";
  size?: "sm" | "md" | "lg" | "xl";
  /** Override icon size in pixels (for photo/avatar variants). Takes precedence over named size. */
  sizePixels?: number;
  onClick?: () => void;
  /** Renders the pill as a removable selection with a trailing close icon. */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  showName?: boolean;
  isActive?: boolean;
  /** Whether the participant is currently selected (adds glow/emphasis) */
  isSelected?: boolean;
  /** Color used for selection glow */
  selectedColor?: string;
}

// Maps ParticipantBadge sizes → Badge size prop + icon pixel size
const sizeMap = {
  sm: { icon: 18, badgeSize: "xs" as const },
  md: { icon: 36, badgeSize: "sm" as const },
  lg: { icon: 48, badgeSize: "md" as const },
  xl: { icon: 64, badgeSize: "md" as const },
};

export const ParticipantBadge: React.FC<ParticipantBadgeProps> = ({
  participant,
  variant = "pill",
  size = "md",
  sizePixels,
  onClick,
  onRemove,
  removeLabel,
  className = "",
  style = {},
  showName = false,
  isActive = true,
  isSelected = false,
  selectedColor,
}) => {
  const colorId = participant.metadata?.colorId;
  const customColor = participant.metadata?.customColor;
  const colorSchema = getParticipantColor(colorId, customColor);

  const isEliminated = participant.status === "eliminated";
  const handleClick = onRemove ?? onClick;
  const isClickable = !!handleClick;
  const s = sizeMap[size];
  const iconSize = sizePixels || s.icon;

  const getInitials = () => {
    if (participant.alias) return participant.alias.substring(0, 3).toUpperCase();
    if (!participant.name) return "?";
    const parts = participant.name.split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return participant.name.substring(0, 2).toUpperCase();
  };

  // Variante: PILL
  if (variant === "pill") {
    const isStriped = colorId?.includes("_striped");
    return (
      <Badge
        variant="outline"
        size={s.badgeSize}
        render={
          onRemove ? (
            <button
              type="button"
              aria-label={removeLabel || `Deselect ${participant.alias || participant.name}`}
            />
          ) : undefined
        }
        onClick={handleClick}
        className={cn(
          "uppercase border-0 relative z-[1]",
          isClickable && "cursor-pointer hover:brightness-110 active:brightness-90",
          isEliminated && "grayscale opacity-60 line-through",
          className,
        )}
        style={{
          background: isActive && !isStriped ? colorSchema.bg : "transparent",
          color: isActive ? (colorSchema.isDark ? "#fff" : "#000") : "#fff",
          border: isStriped ? "none" : `1px solid ${colorSchema.border}`,
          ...style,
        }}
        title={participant.name}
      >
        {isActive && isStriped && (
          <div
            className="absolute inset-0 -z-[1] rounded-md"
            style={{
              background: colorSchema.stripePattern,
              padding: "4px",
            }}
          >
            <div className="size-full rounded-sm" style={{ background: colorSchema.bg }} />
          </div>
        )}
        <span className={cn("relative z-[2]", isStriped && "px-1")}>
          {participant.alias || participant.name}
        </span>
        {onRemove ? <XIcon data-icon="inline-end" aria-hidden="true" /> : null}
      </Badge>
    );
  }

  // Variante: AVATAR / PHOTO
  const isPhotoVariant = variant === "photo" && participant.image;

  return (
    <div className={cn("flex items-center gap-2", className)} style={style}>
      <div
        onClick={onClick}
        className={cn(
          "flex items-center justify-center shrink-0 rounded-full overflow-hidden font-bold",
          isClickable && "cursor-pointer hover:scale-105 transition-transform",
        )}
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          background: colorSchema.bg,
          color: "#fff",
          fontSize: `${iconSize * 0.35}px`,
          boxShadow: isSelected
            ? `0 0 0 3px ${selectedColor || colorSchema.bg}, 0 0 15px ${colorSchema.bg}80`
            : isEliminated
              ? "none"
              : `0 0 0 1px ${colorSchema.border}, 0 2px 4px rgba(0,0,0,0.3)`,
          padding: isPhotoVariant ? "2px" : "0px",
          filter: isEliminated ? "grayscale(1) opacity(0.5)" : "none",
        }}
        title={participant.name}
      >
        {isPhotoVariant ? (
          <img
            src={participant.image}
            alt={participant.name}
            draggable={false}
            className="size-full rounded-full border-0 bg-black object-cover select-none pointer-events-none"
            style={{
              // @ts-ignore
              WebkitUserDrag: "none",
            }}
            onError={(e) => {
              const el = e.target as HTMLImageElement;
              const parent = el.parentElement;
              if (!parent) return;
              el.style.display = "none";
              parent.style.padding = "0px";
              parent.textContent = getInitials();
            }}
          />
        ) : (
          <span className="drop-shadow-sm">{getInitials()}</span>
        )}
      </div>

      {showName && (
        <span
          className={cn(
            s.badgeSize === "xs"
              ? "text-[0.65rem]"
              : s.badgeSize === "sm"
                ? "text-[0.65rem]"
                : "text-xs",
            "font-medium",
          )}
        >
          {participant.alias || participant.name}
        </span>
      )}
    </div>
  );
};
